import React from "react";
import { createPortal } from "react-dom";
import { ExternalLink, X } from "lucide-react";
import { useAperturaAnimada } from "../../hooks/useAperturaAnimada";
import { URL_SUSCRIPCIONES_MERCADOPAGO } from "../../lib/cobroRechazado";

/**
 * Cómo cambiar la tarjeta de una suscripción de MercadoPago
 * (ADR-2026-11-03-cambiar-tarjeta-de-mercadopago-con-instrucciones). La tarjeta no se cambia por API:
 * se cambia en la cuenta de MP, y mandar a la persona directo a MP la dejaba sin saber adónde ir en
 * la app. Lo abren "Cambiar tarjeta" de Configuración → Suscripción y "Cambiar tarjeta en
 * MercadoPago" del aviso de cobro rechazado.
 *
 * Portal al `body` y z por encima de `ModalDeCobro` (z-[200]), que lo puede abrir desde adentro. El
 * Escape y el Tab se atienden en captura y no siguen: cierra éste y no también el de abajo, y el
 * foco no lo pelean los dos.
 */
const SALIDA_MS = 150;

export const ModalTarjetaMercadoPago: React.FC<{
  abierto: boolean;
  onCerrar: () => void;
}> = ({ abierto, onCerrar }) => {
  const { montado, visible } = useAperturaAnimada(abierto, SALIDA_MS);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const irRef = React.useRef<HTMLButtonElement>(null);
  const cerrarRef = React.useRef(onCerrar);
  cerrarRef.current = onCerrar;

  // Foco de diálogo, como `ModalGoogleEnApp`: entra en "Ir a MercadoPago", Tab no se escapa a lo de
  // atrás, y al cerrar vuelve al botón que lo abrió. Con `montado`: en el render en que `abierto`
  // pasa a true el panel todavía no está en el DOM.
  const enPantalla = abierto && montado;
  React.useEffect(() => {
    if (!enPantalla) return;
    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    irRef.current?.focus({ preventScroll: true });
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        cerrarRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      e.stopPropagation();
      const botones = Array.from(panelRef.current.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
      if (botones.length === 0) return;
      const primero = botones[0];
      const ultimo = botones[botones.length - 1];
      const dentro = panelRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === primero || !dentro)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (document.activeElement === ultimo || !dentro)) {
        e.preventDefault();
        primero.focus();
      }
    };
    window.addEventListener("keydown", alTeclear, true);
    return () => {
      window.removeEventListener("keydown", alTeclear, true);
      const activo = document.activeElement;
      const sinFocoPropio = !activo || activo === document.body || panelRef.current?.contains(activo);
      if (sinFocoPropio && previo?.isConnected) previo.focus({ preventScroll: true });
    };
  }, [enPantalla]);

  if (!montado) return null;

  const irAMercadoPago = () => {
    window.open(URL_SUSCRIPCIONES_MERCADOPAGO, "_blank", "noopener,noreferrer");
    onCerrar();
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[210] flex items-center justify-center bg-black/30 px-4 transition-opacity ease-out ${
        visible ? "opacity-100 duration-300" : "opacity-0 duration-150"
      }`}
      // Sin propagar: en React el click de un portal sube por el árbol de componentes, y el aviso
      // que lo abrió puede vivir adentro de otro modal o de algo clickeable.
      onClick={(e) => {
        e.stopPropagation();
        onCerrar();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-tarjeta-mp-titulo"
        onClick={(e) => e.stopPropagation()}
        className={`relative flex max-h-[calc(100dvh-2rem)] w-full max-w-[400px] flex-col rounded-lg bg-white shadow-xl transition-[opacity,transform,filter] ease-out ${
          visible
            ? "opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0"
            : "opacity-0 duration-150 motion-safe:translate-y-3 motion-safe:blur-[4px]"
        }`}
      >
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-10 rounded-sm p-1.5 text-brand-black/40 transition-colors hover:text-brand-black/70"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-4">
          <h2 id="modal-tarjeta-mp-titulo" className="pr-8 text-[16px] font-semibold text-brand-black">
            Cambiá la tarjeta en MercadoPago
          </h2>
          <p className="mt-1 text-[13.5px] leading-snug text-brand-black/60">
            Tu plan se cobra desde tu cuenta de MercadoPago, así que la tarjeta se cambia ahí.
          </p>

          <ol className="mt-4 space-y-2 text-[14px] leading-snug text-brand-black/80">
            {[
              <>
                Abrí la app de MercadoPago y tocá <span className="font-semibold text-brand-black">Más</span>, abajo a la
                derecha.
              </>,
              <>
                Entrá a <span className="font-semibold text-brand-black">Suscripciones</span>.
              </>,
              <>Elegí la suscripción de Atlas y cambiá el medio de pago.</>,
            ].map((paso, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-sm bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-[12px] font-semibold text-brand-primary">
                  {i + 1}
                </span>
                <span>{paso}</span>
              </li>
            ))}
          </ol>

          <img
            src="/images/ayuda/mercadopago-suscripciones.webp"
            width={640}
            height={740}
            alt="Menú Más de la app de MercadoPago, con la opción Suscripciones marcada"
            className="mx-auto mt-4 h-auto w-full max-w-[280px] rounded-md border border-neutral-200"
          />
        </div>

        <div className="border-t border-neutral-100 p-4">
          <button
            ref={irRef}
            type="button"
            onClick={irAMercadoPago}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-6 py-3 text-[15px] font-semibold text-white shadow-sm transition-[background-color,transform] duration-150 ease-out hover:bg-brand-primary-hover active:scale-[0.96]"
          >
            Ir a MercadoPago
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
